import React, { useState, useEffect } from "react";
import { showNotice } from "../helper/notify";
import {
  Button,
  Col,
  Row,
  Table,
  Tag,
  Tooltip,
  Dropdown,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  message,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  DownCircleFilled,
  PlayCircleOutlined,
  FolderOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import PageHeader from "../components/generic/PageHeader";
import {
  getAllUISuites,
  createUISuite,
  updateUISuite,
  deleteUISuite,
  runUISuite,
} from "../service/uitest.service";
import CitrixRunModal from "../components/modal/CitrixRunModal";
import { pollJobStatus } from "../service/run.service";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import "../styles/db.css";

const { Option } = Select;

function UIAutomation() {
  const [suites, setSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSuite, setEditingSuite] = useState(null);
  const [form] = Form.useForm();
  const engine = Form.useWatch("engine", form) || "WEB";
  const [citrixRunSuite, setCitrixRunSuite] = useState(null);
  const navigate = useNavigate();
  const { email } = useUser();

  const fetchSuites = () => {
    setLoading(true);
    getAllUISuites()
      .then((res) => setSuites(res.data || []))
      .catch(() => message.error("Failed to load UI suites"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSuites();
  }, []);

  const openCreateModal = () => {
    setEditingSuite(null);
    form.resetFields();
    form.setFieldsValue({
      engine: "WEB",
      browser: "chromium",
      headless: true,
      viewportWidth: 1920,
      viewportHeight: 1080,
      timeoutMs: 30000,
    });
    setModalOpen(true);
  };

  const openEditModal = (suite) => {
    setEditingSuite(suite);
    form.setFieldsValue({
      engine: suite.engine || "WEB",
      suiteName: suite.suiteName,
      description: suite.description,
      baseUrl: suite.baseUrl,
      browser: suite.browser,
      headless: suite.headless,
      viewportWidth: suite.viewportWidth,
      viewportHeight: suite.viewportHeight,
      timeoutMs: suite.timeoutMs,
      screenshotPath: suite.screenshotPath,
    });
    setModalOpen(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const payload = { ...values, createdBy: email };
      const action = editingSuite
        ? updateUISuite(editingSuite.suiteId, payload)
        : createUISuite(payload);

      action
        .then(() => {
          message.success(editingSuite ? "Suite updated" : "Suite created");
          setModalOpen(false);
          fetchSuites();
        })
        .catch(() => message.error("Failed to save suite"));
    });
  };

  const handleDelete = (suiteId) => {
    deleteUISuite(suiteId)
      .then(() => {
        message.success("Suite deleted");
        fetchSuites();
      })
      .catch(() => message.error("Failed to delete suite"));
  };

  const handleRun = (suite) => {
    // Citrix suites open the data-driven run dialog (environment + spreadsheet)
    if (suite.engine === "CITRIX") {
      setCitrixRunSuite(suite);
      return;
    }
    runUISuite(suite.suiteId)
      .then((res) => {
        const { jobId } = res.data;
        message.info(`Job ${jobId} started`);
        pollJobStatus(jobId)
          .then(() =>
            showNotice(
              "success",
              "UI Test Complete",
              `Suite "${suite.suiteName}" passed.`,
            ),
          )
          .catch((jobData) =>
            showNotice(
              "error",
              "UI Test Failed",
              `Suite "${suite.suiteName}": ${jobData?.errorDescription || "Unknown error"}`,
            ),
          );
      })
      .catch(() => message.error("Failed to start suite"));
  };

  const handleEdit = (suite) => {
    navigate("/ui-automation/builder", { state: { suite } });
  };

  const columns = [
    {
      title: "Suite Name",
      dataIndex: "suiteName",
      key: "suiteName",
      sorter: (a, b) => a.suiteName.localeCompare(b.suiteName),
      render: (text, record) => (
        <a onClick={() => handleEdit(record)} style={{ cursor: "pointer" }}>
          {text}
        </a>
      ),
    },
    {
      title: "Engine",
      dataIndex: "engine",
      key: "engine",
      width: "9%",
      render: (text) => {
        const e = text || "WEB";
        return (
          <Tag color={e === "CITRIX" ? "purple" : "blue"}>
            {e === "CITRIX" ? "Citrix" : "Web"}
          </Tag>
        );
      },
      filters: [
        { text: "Web", value: "WEB" },
        { text: "Citrix", value: "CITRIX" },
      ],
      onFilter: (value, record) => (record.engine || "WEB") === value,
    },
    {
      title: "Base URL",
      dataIndex: "baseUrl",
      key: "baseUrl",
      render: (text) => (
        <span style={{ fontSize: 12, color: "#888" }}>{text || "—"}</span>
      ),
    },
    {
      title: "Browser",
      dataIndex: "browser",
      key: "browser",
      width: "10%",
      render: (text) => (
        <Tag
          color={
            text === "chromium"
              ? "blue"
              : text === "firefox"
                ? "orange"
                : "green"
          }
        >
          {text}
        </Tag>
      ),
      filters: [
        { text: "chromium", value: "chromium" },
        { text: "firefox", value: "firefox" },
        { text: "webkit", value: "webkit" },
      ],
      onFilter: (value, record) => record.browser === value,
    },
    {
      title: "Headless",
      dataIndex: "headless",
      key: "headless",
      width: "10%",
      render: (val) => (
        <Tag color={val ? "default" : "purple"}>
          {val ? "Headless" : "Headed"}
        </Tag>
      ),
    },
    {
      title: "Screenshots",
      dataIndex: "screenshotPath",
      key: "screenshotPath",
      width: "16%",
      render: (text) =>
        text ? (
          <Tooltip title={text}>
            <span style={{ fontSize: 11, color: "#888" }}>
              <FolderOutlined style={{ marginRight: 4 }} />
              {text.length > 24 ? "…" + text.slice(-21) : text}
            </span>
          </Tooltip>
        ) : (
          <span style={{ color: "#bbb", fontSize: 12 }}>Not configured</span>
        ),
    },
    {
      title: "Created",
      dataIndex: "createdDate",
      key: "createdDate",
      width: "15%",
      defaultSortOrder: "descend",
      sorter: (a, b) =>
        (a.createdDate || "").localeCompare(b.createdDate || ""),
    },
    {
      title: "Actions",
      key: "actions",
      width: "12%",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "1",
                label: "Edit Steps",
                onClick: () => handleEdit(record),
              },
              {
                key: "2",
                label: "Edit Settings",
                onClick: () => openEditModal(record),
              },
              {
                key: "3",
                label: (
                  <span style={{ color: "#52c41a" }}>
                    <PlayCircleOutlined /> Run
                  </span>
                ),
                onClick: () => handleRun(record),
              },
              {
                key: "4",
                label: (
                  <Popconfirm
                    title="Delete this suite?"
                    description="All steps will be deleted. This cannot be undone."
                    okText="Delete"
                    okType="danger"
                    cancelText="Cancel"
                    onConfirm={() => handleDelete(record.suiteId)}
                  >
                    <span style={{ color: "#ff4d4f" }}>Delete</span>
                  </Popconfirm>
                ),
              },
            ],
          }}
          overlayClassName="table-row-actions-dropdown"
        >
          <a className="table-more-options">
            Options <DownCircleFilled />
          </a>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="db-container">
      <PageHeader
        icon={<RobotOutlined />}
        title="Application Automation"
        tooltip="Build and run no-code automation for web and desktop (Citrix) applications"
        actions={
          <Button
            className="db-add"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
          >
            New Suite
          </Button>
        }
      />

      <Row className="table-container">
        <Table
          columns={columns}
          dataSource={suites}
          rowKey="suiteId"
          loading={loading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Row>

      {/* ─── Create / Edit suite modal ──────────────────────── */}
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={editingSuite ? "Edit Suite Settings" : "New Automation Suite"}
        onOk={handleSave}
        okText="Save"
        destroyOnClose
        centered
        width={560}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="engine"
            label="Engine"
            initialValue="WEB"
            extra={
              !editingSuite && (
                <span style={{ fontSize: 11, color: "#888" }}>
                  <b>Web</b> runs in-browser via Playwright on the ARC server.{" "}
                  <b>Citrix</b> drives a desktop/Citrix app (e.g. CRIMS) via an
                  on-prem agent using image &amp; OCR matching.
                </span>
              )
            }
          >
            <Select disabled={!!editingSuite}>
              <Option value="WEB">Web (Playwright)</Option>
              <Option value="CITRIX">Citrix / Desktop (OculiX)</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="suiteName"
            label="Suite Name"
            rules={[{ required: true, message: "Suite name is required" }]}
          >
            <Input placeholder="e.g. Login Flow" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="What does this suite test?" />
          </Form.Item>

          {engine === "CITRIX" && (
            <div
              style={{
                background: "var(--color-background-secondary, #f5f7fa)",
                border: "1px solid var(--color-border-tertiary, #e4e9f0)",
                borderRadius: 6,
                padding: "10px 12px",
                fontSize: 12,
                color: "#555",
                marginBottom: 8,
              }}
            >
              Build steps with image &amp; OCR actions in the <b>Step builder</b>
              . Pick the <b>environment</b> and attach a trade spreadsheet when
              you run — each row becomes its own run. Execution is delegated to
              an online agent on the client's Windows/Citrix VM.
            </div>
          )}

          {engine !== "CITRIX" && (
            <>
          <Form.Item
            name="baseUrl"
            label="Base URL"
            rules={[{ required: true, message: "Base URL is required" }]}
          >
            <Input placeholder="https://www.archub.com.au" />
          </Form.Item>

          <Form.Item
            name="screenshotPath"
            label="Screenshot Folder"
            extra={
              <span style={{ fontSize: 11, color: "#888" }}>
                Folder on the ARC server where screenshots are saved. Each run
                creates: <code>{"<folder>/<suite>/<timestamp>/"}</code>. Leave
                blank to disable. e.g. <code>C:\ARC_APP\screenshots</code> or{" "}
                <code>/opt/arc/screenshots</code>
              </span>
            }
          >
            <Input
              prefix={<FolderOutlined style={{ color: "#bbb" }} />}
              placeholder="C:\ARC_APP\screenshots"
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="browser" label="Browser">
                <Select>
                  <Option value="chromium">Chromium</Option>
                  <Option value="firefox">Firefox</Option>
                  <Option value="webkit">WebKit</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="headless"
                label="Run Headless"
                valuePropName="checked"
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="timeoutMs" label="Timeout (ms)">
                <InputNumber
                  min={1000}
                  max={120000}
                  step={1000}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="viewportWidth" label="Viewport Width">
                <InputNumber min={320} max={3840} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="viewportHeight" label="Viewport Height">
                <InputNumber min={240} max={2160} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
            </>
          )}
        </Form>
      </Modal>

      {/* ─── Citrix data-driven run (reusable) ────────────────── */}
      <CitrixRunModal
        open={!!citrixRunSuite}
        onClose={() => setCitrixRunSuite(null)}
        suite={citrixRunSuite}
      />
    </div>
  );
}

export default UIAutomation;
